import React from 'react';
import { useAccountStore } from '../../../../../components/Stores/accountStore';
import { OptionSelect } from '../../../../../components/UI-Elements/Select/OptionSelect';
import { Material } from '../../../../../sp-types';
import { IClientExposure, IFutureOrder } from '../../../../../sp-types/FutureOrderType';
import { formatToShortString } from '../../../../../utils/FormatValue';
import './ProductMaterialTable.css'; // Importing CSS file
import { getUnitObj } from '../../../../../utils/MassUnits/massUnitsUtils';

interface EditFutureOrderTableProps {
  materialsMap: Record<string, Material>;
  editedFutureOrder: IFutureOrder;
  showAdjustments?: boolean;
  materialIndexChanged?: (_index: string, _matId: string) => void;
  pricesMap: Record<string, number>;
}
export const ClientExposureEditTable: React.FC<EditFutureOrderTableProps> = ({
  showAdjustments = true,
  materialIndexChanged = dummy,
  editedFutureOrder,
  materialsMap,
  pricesMap,
}) => {
  const { clientExposure } = editedFutureOrder;
  const { materialColorDictionary, account } = useAccountStore();
  const [materialIndexMap, setMaterialIndexMap] = React.useState<Record<string, IClientExposure>>(
    editedFutureOrder.clientExposure,
  );
  const rawMaterialExposureToTableData = (
    editedFutureOrderData: Record<string, IClientExposure>,
    priceMap: Record<string, number>,
  ): MaterialData[] => {
    const materialData: MaterialData[] = Object.entries(editedFutureOrderData).map(
      ([materialId, { basePrice, weightInProduct }]) => ({
        basePrice,
        materialId,
        weightInProduct,
        material: materialsMap[materialId].name,
        materialColor: materialColorDictionary[materialId],
        priceIndex: materialsMap[materialId].priceIndex,
        quantity: weightInProduct * editedFutureOrder.orderDetails.quantity,
        totalPrice: weightInProduct * editedFutureOrder.orderDetails.quantity * priceMap[materialId],
      }),
    );
    return materialData;
  };

  const columnHeaders = ['Material', 'Weight per unit', 'Total Weight', 'Total price', 'Price Index'];

  return (
    <table className="materials-table">
      <thead>
        <tr>
          {columnHeaders.map((header, i) => (
            <th key={'h' + i}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rawMaterialExposureToTableData(clientExposure, pricesMap).map((item, index) => (
          <tr key={index}>
            <td className="ps-0">
              <div className="ps-[0.5rem]" style={{ borderLeft: `6px solid ${item.materialColor}` }}>
                {item.material}
              </div>
            </td>
            {!showAdjustments && (
              <td>
                {formatToShortString(item.weightInProduct, 3)}{' '}
                {getUnitObj(materialsMap[item.materialId].unit).shortForm}
              </td>
            )}
            <td>
              {formatToShortString(item.quantity, 0)} {getUnitObj(materialsMap[item.materialId].unit).shortForm}
            </td>
            <td>${formatToShortString(item.totalPrice, 0)} USD</td>
            <td>
              <div className="w-52">
                <OptionSelect
                  currentOption={materialIndexMap[item.materialId]?.priceIndex || defaultAdjustment}
                  options={account.AdjustmentsInfo.productPriceIndexes}
                  setCurrentOption={(priceIndex: string) => {
                    setMaterialIndexMap((currMap) => ({
                      ...currMap,
                      [item.materialId]: { ...currMap[item.materialId], priceIndex },
                    }));
                    materialIndexChanged(priceIndex, item.materialId);
                  }}
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface MaterialData {
  material: string;
  materialId: string;
  materialColor: string;
  quantity: number;
  basePrice: number;
  weightInProduct: number;
  totalPrice: number;
  priceIndex: string;
}
const defaultAdjustment = 'Trailing month';

const dummy = () => null;
